import React from "react"
import dayjs from "dayjs"
import { marked } from "marked"
import DOMPurify from "dompurify"
import { TIME_PERIOD_MAP, PERIOD_MAP_REVERSE, DATE_RANGE_FILTERS, DATE_SINGLE_FILTERS, DEFAULT_PERIOD_LABEL, CUSTOM_PERIOD } from "./constants"

export function markdownToNode(text) {
  if (!text) return null
  const html = DOMPurify.sanitize(marked.parse(text))
  return <div dangerouslySetInnerHTML={{ __html: html }} />
}

// ── Period params ──────────────────────────────────────────────────────────────

export function getTimezone() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
  return tz === "Asia/Calcutta" ? "Asia/Kolkata" : tz
}

export function buildPeriodParams(period, dateRange, singleDate) {
  const timezone = getTimezone()
  const params = { timezone }
  if (singleDate) {
    params.date_range = singleDate.format("YYYY-MM-DD")
    params.period = CUSTOM_PERIOD
  } else if (dateRange?.[0] && dateRange?.[1]) {
    params.date_range = `${dateRange[0].format("YYYY-MM-DD")}T${dateRange[1].format("YYYY-MM-DD")}`
    params.period = CUSTOM_PERIOD
  } else {
    params.period = TIME_PERIOD_MAP[period] || "last_30_days"
  }
  return params
}

// ── KPI stats builder ──────────────────────────────────────────────────────────

export function buildStats(kpiData, periodLabel = DEFAULT_PERIOD_LABEL) {
  const extract = (field) => {
    if (field == null) return { value: null, trend: null, description: null }
    if (typeof field === "object") {
      // trend is null when there is no valid prior-year baseline to compare against
      const hasComparison = field.comparison_available !== false && (field.percentage_change ?? field.trend) != null
      return {
        value: field.value ?? field.result ?? null,
        trend: hasComparison ? (field.percentage_change ?? field.trend) : null,
        description: field.description ?? null,
      }
    }
    return { value: field, trend: null, description: null }
  }
  const fmtMoney = (val) => val == null ? "-" : `$${Number(val).toLocaleString()}`
  const fmtNum = (val) => val == null ? "-" : String(val)

  const grossRevenue = extract(kpiData?.gross_revenue)
  const expenses = extract(kpiData?.expenses)
  const profit = extract(kpiData?.net_profit)
  const bookings = extract(kpiData?.total_bookings)
  const cancellations = extract(kpiData?.cancellations)

  return [
    { label: "GROSS REVENUE", value: fmtMoney(grossRevenue.value), trend: grossRevenue.trend, period: periodLabel, description: grossRevenue.description },
    { label: "EXPENSES", value: fmtMoney(expenses.value), trend: expenses.trend, period: periodLabel, description: expenses.description },
    { label: "OPERATING PROFIT", value: fmtMoney(profit.value), trend: profit.trend, period: periodLabel, description: profit.description },
    { label: "TOTAL BOOKINGS", value: fmtNum(bookings.value), trend: bookings.trend, period: periodLabel, description: bookings.description },
    { label: "CANCELATIONS", value: fmtNum(cancellations.value), trend: cancellations.trend, period: periodLabel, description: cancellations.description }
  ]
}

// ── Saved period restorer ──────────────────────────────────────────────────────

export function resolveSavedPeriod(state) {
  const savedPeriod = state?.global_period
  const rawDateRange = state?.date_range

  if (savedPeriod === "custom" && rawDateRange) {
    // A same-day selection is stored as "DATE" or "DATETDATE" — both restore as a
    // one-day range so the "Custom" tab stays selected after a refresh.
    const [start, end = start] = rawDateRange.split("T")
    return { type: "range", range: [dayjs(start), dayjs(end)], label: "Custom" }
  }

  return { type: "preset", label: PERIOD_MAP_REVERSE[savedPeriod] || "This month" }
}

// ── Chart range builders ───────────────────────────────────────────────────────

export function buildHourlyRange(items) {
  const slots = Array.from({ length: 24 }, (_, h) => ({ key: h, label: String(h).padStart(2, "0") + ":00" }))
  const hourMap = {}
  items.forEach((d) => { hourMap[d.hour ?? Number.parseInt(d.time)] = d.total ?? 0 })
  return { labels: slots.map((s) => s.label), values: slots.map((s) => hourMap[s.key] ?? 0) }
}

export function buildYearlyRange(items) {
  const dataMap = {}
  items.forEach((d) => { dataMap[d.year ?? d.date?.slice(0, 4)] = d.total ?? d.revenue ?? 0 })
  const keys = Object.keys(dataMap).sort((a, b) => a.localeCompare(b))
  return { labels: keys, values: keys.map((k) => dataMap[k] ?? 0) }
}

export function buildMonthlyRange(period, dateRange, items) {
  const today = dayjs()
  let startM, endM
  if (period === "Last year") {
    startM = today.subtract(1, "year").startOf("year")
    endM = today.subtract(1, "year").endOf("year")
  } else if (dateRange?.[0] && dateRange?.[1]) {
    startM = dateRange[0].startOf("month")
    endM = dateRange[1]
  } else {
    startM = today.startOf("year")
    endM = today
  }
  const slots = []
  let cur = startM.startOf("month")
  while (cur.isBefore(endM) || cur.isSame(endM, "month")) {
    slots.push({ key: cur.format("YYYY-MM"), label: cur.format("MMM") })
    cur = cur.add(1, "month")
  }
  const monthMap = {}
  items.forEach((d) => { monthMap[d.month ?? d.date?.slice(0, 7)] = d.total ?? d.revenue ?? 0 })
  return { labels: slots.map((s) => s.label), values: slots.map((s) => monthMap[s.key] ?? 0) }
}

export function buildDailyRange(period, dateRange, items) {
  const today = dayjs()
  let start, end
  if (period === "Last 7 days")       { start = today.subtract(6, "day");  end = today }
  else if (period === "Last 30 days") { start = today.subtract(29, "day"); end = today }
  else if (period === "Last 90 days") { start = today.subtract(89, "day"); end = today }
  else if (period === "This month")   { start = today.startOf("month");    end = today }
  else if (dateRange?.[0] && dateRange?.[1]) { start = dateRange[0]; end = dateRange[1] }
  else {
    return { labels: items.map((d) => d.date), values: items.map((d) => d.total ?? 0) }
  }
  const slots = []
  let cur = start
  while (cur.isBefore(end, "day") || cur.isSame(end, "day")) {
    slots.push({ key: cur.format("YYYY-MM-DD"), label: cur.format("MMM D") })
    cur = cur.add(1, "day")
  }
  const dateMap = {}
  items.forEach((d) => { dateMap[d.date] = d.total ?? 0 })
  return { labels: slots.map((s) => s.label), values: slots.map((s) => dateMap[s.key] ?? 0) }
}

export function buildRevenueRange(group_by, period, dateRange, items) {
  if (group_by === "hourly")  return buildHourlyRange(items)
  if (group_by === "yearly")  return buildYearlyRange(items)
  if (group_by === "monthly") return buildMonthlyRange(period, dateRange, items)
  return buildDailyRange(period, dateRange, items)
}

// ── Misc helpers ───────────────────────────────────────────────────────────────

export function formatMonthLabel(month) {
  const [year, m] = month.split("-")
  const label = new Date(Number(year), Number(m) - 1).toLocaleString("en-US", { month: "short" })
  return `${label}'${year.slice(2)}`
}

export function buildSlotBlocks(totalSlots, bookedSlots) {
  const displayTotal = Math.min(totalSlots, 20)
  return Array.from({ length: displayTotal }, (_, i) => i < Math.min(bookedSlots, displayTotal))
}

export function normalizeChartLabel(l) {
  if (/^\d{4}-\d{2}$/.test(l)) return formatMonthLabel(l)
  return l
}

function toLocalTimeString(utcString) {
  return new Date(utcString + "Z").toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
}

// History payloads store source_prompt as a JSON string (holding value_format,
// description, etc.); live responses may pass it as an object. Parse defensively.
function parseSourcePrompt(sourcePrompt) {
  if (!sourcePrompt) return {}
  if (typeof sourcePrompt === "object") return sourcePrompt
  try {
    return JSON.parse(sourcePrompt)
  } catch {
    return {}
  }
}

// The history API nests chart/option fields under `metadata`, whereas the live
// chat response returns them at the top level. Read from metadata first, falling
// back to the top level so both shapes map to the same message structure.
export function mapHistoryToMessages(history) {
  return history.map((item, i) => {
    const meta = item.metadata ?? {}
    const chartType = meta.chart_type ?? item.chart_type
    const chartData = meta.chart_data ?? item.chart_data
    const hasChart = chartData != null && chartType != null
    const options = meta.options ?? item.options ?? null
    const hasOptions = options?.items?.length > 0
    // value_format isn't a top-level history field — it lives inside source_prompt.
    const sourcePrompt = meta.source_prompt ?? item.source_prompt
    const valueFormat = meta.value_format ?? item.value_format ?? parseSourcePrompt(sourcePrompt).value_format ?? "integer"

    const message = {
      id: item.id ?? i,
      sender: item.role === "user" ? "user" : "ai",
      text: item.message ?? "",
      time: item.created_at ? toLocalTimeString(item.created_at) : "",
      chartPayload: hasChart
        ? {
            chart_title: meta.chart_title ?? item.chart_title,
            chart_type: chartType,
            chart_data: chartData,
            source_prompt: sourcePrompt,
            value_format: valueFormat,
            options: hasOptions ? options : null,
          }
        : null,
      // A clarification (no chart) surfaces its options as selectable tiles.
      options: !hasChart && hasOptions ? options : null,
    }

    // If the prompt/clarification was already answered, the next history item is a
    // user message whose text matches one of the option labels. Render the resolved
    // state instead of live-actionable buttons (which would risk re-triggering it).
    if (hasOptions) {
      const next = history[i + 1]
      const answered = next?.role === "user" ? options.items.find((o) => o.label === next.message) : null
      if (answered) {
        if (hasChart) {
          message.chartPromptDismissed = true
          message.chartSelectedOption = answered.label
        } else {
          message.selectedOption = answered.value
        }
      }
    }

    return message
  })
}

export function getActivePeriodLabel(activeDateFilter, dateRange, singleDate) {
  if (DATE_RANGE_FILTERS.includes(activeDateFilter) && dateRange[0] && dateRange[1])
    return `${dateRange[0].format("MMM D")} – ${dateRange[1].format("MMM D")}`
  if (DATE_SINGLE_FILTERS.includes(activeDateFilter) && singleDate)
    return singleDate.format("MMM D, YYYY")
  return activeDateFilter
}

export function getWidgetData(widgetId, dashboardData) {
  const entry = dashboardData?.[widgetId]
  if (entry && typeof entry === "object" && "chart_data" in entry) return entry.chart_data
  return entry
}

export function getWidgetMeta(widgetId, dashboardData) {
  const entry = dashboardData?.[widgetId]
  if (entry && typeof entry === "object") {
    const meta = {}
    if ("group_by" in entry) meta.group_by = entry.group_by
    if ("description" in entry) meta.description = entry.description
    if ("chart_title" in entry) meta.title = entry.chart_title
    meta.value_format = entry.value_format ?? "integer"
    return meta
  }
  return {}
}

// ── Value formatter ────────────────────────────────────────────────────────────

export function formatValue(value, format = "currency") {
  if (value == null || value === "None") return ""
  switch (format) {
    case "currency":   return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    case "integer":    return value.toLocaleString("en-US", { maximumFractionDigits: 0 })
    case "decimal":    return value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    case "percentage": return `${value.toFixed(1)}%`
    default:           return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}

// ── Placeholder widgets ────────────────────────────────────────────────────────

export function buildPlaceholderWidgets(widgetsArray) {
  return widgetsArray.map((w) => ({
    id: `_ph_${w.id}`,
    apiId: w.id,
    localId: null,
    colSpan: 1,
    rowSpan: 1,
    type: "placeholder",
    isDefault: w.is_default === true,
  }))
}
